import React from 'react';
import { clsx } from 'clsx';
import { Input } from '../Input';
import { Textarea } from './Textarea';
import { Select } from './Select';
import { MultiSelect } from './MultiSelect';
import { Checkbox } from './Checkbox';
import { RadioGroup } from './Radio';
import { DatePicker } from './DatePicker';

export type DynamicFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'email'
  | 'phone'
  | 'url'
  | 'date'
  | 'datetime'
  | 'select'
  | 'multiselect'
  | 'checkbox'
  | 'radio';

export interface DynamicFieldOption {
  value: string;
  label: string;
  color?: string;
  icon?: string;
  disabled?: boolean;
}

export interface DynamicFieldValidation {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  patternMessage?: string;
}

export interface DynamicFieldProps {
  id: string;
  fieldKey: string;
  name: string;
  fieldType: DynamicFieldType;
  value: any;
  onChange: (value: any) => void;
  options?: DynamicFieldOption[];
  validation?: DynamicFieldValidation;
  placeholder?: string;
  helpText?: string;
  isRequired?: boolean;
  error?: string;
  disabled?: boolean;
  className?: string;
}

export function DynamicField({
  id,
  fieldKey,
  name,
  fieldType,
  value,
  onChange,
  options = [],
  validation = {},
  placeholder,
  helpText,
  isRequired,
  error,
  disabled,
  className,
}: DynamicFieldProps) {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const target = e.target;
    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      onChange(target.checked);
    } else if (target instanceof HTMLInputElement && target.type === 'number') {
      onChange(target.value === '' ? '' : Number(target.value));
    } else {
      onChange(target.value);
    }
  };

  const commonProps = {
    id,
    name: fieldKey,
    disabled,
    className,
  };

  // Map field types to HTML input types
  const getInputType = (type: DynamicFieldType): string => {
    const typeMap: Record<string, string> = {
      text: 'text',
      number: 'number',
      email: 'email',
      phone: 'tel',
      url: 'url',
    };
    return typeMap[type] || 'text';
  };

  switch (fieldType) {
    case 'text':
    case 'email':
    case 'phone':
    case 'url':
      return (
        <Input
          {...commonProps}
          type={getInputType(fieldType)}
          value={value ?? ''}
          onChange={handleInputChange}
          placeholder={placeholder}
          helperText={helpText}
          error={error}
          minLength={validation.minLength}
          maxLength={validation.maxLength}
          required={isRequired}
        />
      );

    case 'number':
      return (
        <Input
          {...commonProps}
          type="number"
          value={value ?? ''}
          onChange={handleInputChange}
          placeholder={placeholder}
          helperText={helpText}
          error={error}
          min={validation.min}
          max={validation.max}
          required={isRequired}
        />
      );

    case 'textarea':
      return (
        <Textarea
          {...commonProps}
          value={value ?? ''}
          onChange={handleInputChange}
          placeholder={placeholder}
          helperText={helpText}
          error={error}
          maxLength={validation.maxLength}
          showCount={!!validation.maxLength}
          required={isRequired}
        />
      );

    case 'select':
      return (
        <Select
          {...commonProps}
          value={value ?? ''}
          onChange={handleInputChange}
          options={options.map((opt) => ({
            value: opt.value,
            label: opt.label,
            disabled: opt.disabled,
          }))}
          placeholder={placeholder || 'Select an option...'}
          helperText={helpText}
          error={error}
          required={isRequired}
        />
      );

    case 'multiselect':
      return (
        <MultiSelect
          options={options.map((opt) => ({
            value: opt.value,
            label: opt.label,
            disabled: opt.disabled,
          }))}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          placeholder={placeholder || 'Select options...'}
          helperText={helpText}
          error={error}
          disabled={disabled}
          className={className}
        />
      );

    case 'checkbox':
      return (
        <Checkbox
          {...commonProps}
          checked={!!value}
          onChange={handleInputChange}
          label={name}
          description={helpText}
          error={error}
        />
      );

    case 'radio':
      return (
        <RadioGroup
          name={fieldKey}
          options={options.map((opt) => ({
            value: opt.value,
            label: opt.label,
            disabled: opt.disabled,
          }))}
          value={value ?? ''}
          onChange={onChange}
          error={error}
          className={className}
        />
      );

    case 'date':
      return (
        <DatePicker
          {...commonProps}
          value={value ?? ''}
          onChange={handleInputChange}
          helperText={helpText}
          error={error}
          required={isRequired}
          showTime={false}
        />
      );

    case 'datetime':
      return (
        <DatePicker
          {...commonProps}
          value={value ?? ''}
          onChange={handleInputChange}
          helperText={helpText}
          error={error}
          required={isRequired}
          showTime={true}
        />
      );

    default:
      return (
        <Input
          {...commonProps}
          type="text"
          value={value ?? ''}
          onChange={handleInputChange}
          placeholder={placeholder}
          helperText={helpText}
          error={error}
          required={isRequired}
        />
      );
  }
}

// Wrapper with label
interface DynamicFormFieldProps extends DynamicFieldProps {
  showLabel?: boolean;
}

export function DynamicFormField({
  showLabel = true,
  name,
  isRequired,
  helpText,
  error,
  fieldType,
  ...props
}: DynamicFormFieldProps) {
  // For checkbox, don't show external label since it's built-in
  if (fieldType === 'checkbox') {
    return (
      <div className="w-full">
        <DynamicField
          {...props}
          name={name}
          fieldType={fieldType}
          isRequired={isRequired}
          helpText={helpText}
          error={error}
        />
      </div>
    );
  }

  return (
    <div className="w-full">
      {showLabel && (
        <label
          htmlFor={props.id}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {name}
          {isRequired && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <DynamicField
        {...props}
        name={name}
        fieldType={fieldType}
        isRequired={isRequired}
        error={error}
      />
      {helpText && !error && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helpText}</p>
      )}
    </div>
  );
}
